import { DarkAiBrainData } from "@/components/Home/v2/DarkAiBrainSection";
import { PlatformMigrationV2Data } from "@/components/Home/v2/PlatformMigrationV2Section";
import { PricingCoreFeaturesData } from "@/components/Home/v2/PricingCoreFeaturesSection";
import { PricingHeroV2Data } from "@/components/Home/v2/PricingHeroV2";
import { PricingPlansCompareData } from "@/components/Home/v2/PricingPlansCompareSection";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { ProductV2FaqSection } from "@/shared/data/product.v2.data";
import {
  heroPrimaryCta,
  heroSecondaryCta,
} from "@/shared/data/product.v2.data";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { getCDNURL } from "@/shared/utils/commons";

/**
 * pricing.v2.data — data for the V2 pricing page.
 * Composed of: hero → plans-compare → core-features → ai-brain →
 * stories → platform-migration → faq.
 */

// Simple "Get Started" CTA reused on each plan card.
const planGetStartedCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Get Started",
  actionType: CtaActionType.SignUp,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
    eventProperties: {
      source: SignUpSourceEnum.hero,
    },
  },
};

const scheduleCallCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Schedule a Call",
  actionType: CtaActionType.DelayedHrefBlank,
  href: "https://calendly.com/d/cx9y-wsb-7n5/graphy-product-demo-enterprises?",
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.bodyEnd,
    },
  },
};

const bookDemoOnlyCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Book A Demo",
  actionType: CtaActionType.BookDemo,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.bodyEnd,
    },
  },
};

// --- 1. HERO ---------------------------------------------------------------

export const pricingV2HeroData: PricingHeroV2Data = {
  titleMuted: "Start Your",
  titleBright: "14-Day Free Trial Today",
  description:
    "Whether you're a solo creator or a team of 10, Graphy gives you everything you need to launch, scale, and automate your online teaching business — all in one place.",
  bullets: [
    "AI Website Builder",
    "Tools to build and sell digital products",
    "Branded mobile apps",
    "AI sales, support & marketing agents",
    "Unlimited learners",
    "Secure payment gateway and CRM",
  ],
  trustedByLabel: "Trusted by 200,000+ creators",
  reviewBadges: [
    { name: "G2 Crowd", rating: 4.3 },
    { name: "Capterra", rating: 4.7 },
    { name: "Trustpilot", rating: 4.1 },
  ],
  card: {
    image: `${getCDNURL()}graphy-web/V4/images/Pricing Page/Hero Section/Frame 1116611704.png`,
    imageAlt: "Graphy creator",
    startsAtLabel: "Starts at",
    strikePrice: "\u20B924,999",
    freeLabel: "Free",
    durationLabel: "for 14 days",
    ctas: [heroPrimaryCta, heroSecondaryCta],
  },
};

// --- 2. COMPARE PLANS ------------------------------------------------------

export const pricingV2PlansData: PricingPlansCompareData = {
  eyebrow: "PRICING",
  title: "Compare Plans",
  subtitle:
    "Whether you're just starting or scaling fast, there's a plan built for you. No fine print. No hidden limits. Just choose your plan and start.",
  plans: [
    {
      name: "Launch",
      tagline: "Tailored for creators",
      price: "\u20B924,999",
      pricePeriod: "Per Year",
      feeLine: "+ 10% transaction fee per sale or \u20B910 whichever is higher",
      description:
        "Launch your digital products — with everything you need to start selling online.",
      cta: planGetStartedCta,
      featuresHeading: "All core features, plus:",
      features: [
        { label: "AI Website Builder", icon: "sparkles" },
        { label: "2 AI Custom Webpages", icon: "sparkles" },
        { label: "Unlimited Products", icon: "check" },
        { label: "DRM Protection", icon: "check" },
        { label: "Unlimited Contacts", icon: "check" },
        { label: "Unlimited Admin Accounts", icon: "check" },
        { label: "Unlimited Live Classes & Webinars", icon: "check" },
        { label: "5,000 Marketing Emails/Month", icon: "check" },
        { label: "Third Party Integrations", icon: "check" },
      ],
    },
    {
      name: "Grow",
      tagline: "Built for businesses",
      price: "\u20B949,999",
      pricePeriod: "Per Year",
      feeLine: "+ 7.5% transaction fee per sale or \u20B910 whichever is higher",
      description:
        "Scale your business with advanced AI tools designed to convert and retain customers.",
      cta: planGetStartedCta,
      featuresHeading: "Everything in Launch, plus:",
      recommended: true,
      features: [
        { label: "AI Agent for Sales", icon: "sparkles" },
        { label: "AI Agent for Support", icon: "sparkles" },
        { label: "AI Avatar for Tutoring", icon: "sparkles" },
        { label: "AI Landing Pages", icon: "sparkles" },
        { label: "Unlimited AI Custom Pages", icon: "sparkles" },
        { label: "2 Concurrent Live Sessions", icon: "check" },
        { label: "10,000 Marketing Emails/Month", icon: "check" },
        { label: "Remove Graphy Branding", icon: "check" },
        { label: "Personalized Onboarding Assistance", icon: "check" },
        { label: "White-Labeled WhatsApp Marketing", icon: "check" },
      ],
    },
    {
      name: "Rise",
      tagline: "Suited for brands",
      price: "\u20B999,999",
      pricePeriod: "Per Year",
      feeLine: "+ 5% transaction fee per sale or \u20B910 whichever is higher",
      description:
        "Run a high-volume and fully branded business — built for scale, security, and performance.",
      cta: planGetStartedCta,
      featuresHeading: "Everything in Grow, plus:",
      features: [
        { label: "3 Concurrent Live Sessions", icon: "check" },
        { label: "15,000 Marketing Emails/Month", icon: "check" },
        { label: "Both Android and iOS apps", icon: "check" },
        { label: "API & Webhooks Support", icon: "check" },
        { label: "Single Sign On (SSO) Support", icon: "check" },
        { label: "All advanced integrations", icon: "check" },
        { label: "Migration Assistance", icon: "check" },
        { label: "Priority Support & Relationship Manager", icon: "check" },
        { label: "White-Labeled Email Marketing", icon: "check" },
      ],
    },
  ],
  needMore: {
    heading: "Need more?",
    description:
      "For businesses ready to scale with flexibility and hands-on support.",
    card: {
      name: "Unlimited",
      tagline: "Made for larger teams and custom needs",
      cta: scheduleCallCta,
      features: [
        "Flexible pricing tailored to your scale and requirements",
        "End-to-end onboarding and migration support",
      ],
    },
  },
};

// --- 3. CORE FEATURES ------------------------------------------------------

export const pricingV2CoreFeaturesData: PricingCoreFeaturesData = {
  title: "Every plan includes core features",
  features: [
    // col 1 → col 2 → col 3 (interleaved for column-major rendering)
    "AI Website Builder",
    "Real-time sales and conversion tracking",
    "Affiliate tracking baked right in",
    "Your own branded Android mobile app",
    "Deep marketing insights via built-in analytics",
    "Live chat and email support",
    "Accept payments via Stripe or your preferred gateway",
    "SSL certificates for platform security",
    "Run discounts on products",
    "Global selling with country-specific pricing",
    "Custom team roles and admin access",
    "Offer exclusive memberships to superfans",
    "Free custom domain support",
    "Unlimited live sessions and webinars",
    "Award certificates to learners — automatically",
    "Unlimited courses and learners",
    "Unlimited email & push notifications",
    "Create custom coupon codes",
  ],
  ctas: [heroPrimaryCta, heroSecondaryCta],
};

// --- 4. AI BRAIN -----------------------------------------------------------

export const pricingV2AiBrainData: DarkAiBrainData = {
  eyebrow: "AI AGENTS & AVATAR",
  titleMuted: "Build your",
  titleBright: "AI Brain",
  description:
    "Train one AI Brain on your content to power every feature - AI Agents and Avatar and Website Builder.",
  bullets: [
    {
      text: "Your AI Brain powers all your AI Agents — for sales, support, and marketing.",
    },
    {
      text: "AI Avatar and Agents that respond in your tone, logic, and teaching style.",
    },
    {
      text: "Scale conversations, conversions, and support — without hiring or burnout.",
    },
  ],
  ctas: [heroPrimaryCta, heroSecondaryCta],
  closingLine:
    "While you sleep, teach, or take a break — your AI sells, supports, and re-engages learners for you.",
  image: `${getCDNURL()}graphy-web/V4/images/Pricing Page/AI Agent and Avatar Section/image 429.png`,
  imageAlt: "AI Brain powered workflow",
};

// --- 6. PLATFORM MIGRATION -------------------------------------------------

export const pricingV2PlatformMigrationData: PlatformMigrationV2Data = {
  eyebrow: "USING ANOTHER PLATFORM?",
  titleMuted: "We'll move",
  titleBright: "everything",
  titleMutedTail: "over for you",
  description:
    "You're just one conversation away from a smoother, smarter setup. We'll personally migrate:",
  bullets: [
    "All your members (without them noticing a thing)",
    "Your entire course library — videos, PDFs, modules, the works",
    "Sales pages, checkout flows, automations",
    "Your email list — tags, segments, and all",
  ],
  cta: bookDemoOnlyCta,
  image: `${getCDNURL()}graphy-web/V4/images/Pricing Page/Using Other Platform Section/image 428.png`,
  imageAlt: "Migrate your platform to Graphy",
};

// --- 7. FAQ ----------------------------------------------------------------

export const pricingV2FaqData: ProductV2FaqSection = {
  title: "Frequently Asked Questions",
  ctas: [heroPrimaryCta, heroSecondaryCta],
  items: [
    {
      question:
        "Do you take 10% on any type of sale or is it different for different offerings?",
      answer:
        "We take a flat 10% revenue share or Rs. 10 whichever is higher for all types of offerings, subject to some ongoing offers where we might charge even less.",
    },
    {
      question: "Is there a refund policy?",
      answer:
        "Yes, if you end up not finding a use case for the platform, we offer a refund window of 7 days, no questions asked.",
    },
    {
      question: "How can I pay for my Graphy account?",
      answer:
        "We have support for almost all the payment methods in different geographies. Checkout your Graphy dashboard to know more.",
    },
    {
      question: "Are there any academic discounts on Graphy?",
      answer:
        "Please contact our team for more information on any ongoing discounts.",
    },
    {
      question: "How does the payout work?",
      answer:
        "All your earnings from your sales will be instantly credited into your account.",
    },
  ],
};
